import { exec } from 'child_process';
import * as path from 'path';
import * as fs from 'fs';
import { promisify } from 'util';
import { Binary } from './Binary';
import { createRequire } from 'module';

const execAsync = promisify(exec);

interface SqliteResult {
    status: 'success' | 'error';
    message: string;
    data: any | null;
}

interface RunResult {
    changes: number;
    lastID: number;
}

export class SqliteExecutor {
    private binaryPath: string;
    private dbPath: string;

    constructor(dbPath: string) {
        this.dbPath = dbPath;
        this.binaryPath = this.getBinaryPath();
    }

    private getBinaryPath(): string {
        // Try to find the binary in the new location first
        const possibleDirs = [
            path.resolve(process.cwd(), '.dbcube', 'bin'),
            path.resolve(process.cwd(), 'node_modules', '.dbcube', 'bin'),
            path.resolve(__dirname, '..', 'bin'),
        ];

        const platform = process.platform;
        const extension = platform === 'win32' ? '.exe' : '';
        const binaryName = `sqlite-engine-${platform === 'win32' ? 'windows' : platform === 'darwin' ? 'macos' : 'linux'}-x64${extension}`;

        for (const dir of possibleDirs) {
            const fullPath = path.join(dir, binaryName);
            if (fs.existsSync(fullPath)) {
                return fullPath;
            }
        }

        // Fallback to old format if new format not found
        const fallbackName = `sqlite-engine${extension}`;
        for (const dir of possibleDirs) {
            const fullPath = path.join(dir, fallbackName);
            if (fs.existsSync(fullPath)) {
                return fullPath;
            }
        }
        
        // If nothing found, return the preferred path (will trigger download)
        return path.join(possibleDirs[0], binaryName);
    }

    private async executeBinary(args: string[]): Promise<SqliteResult> {
        const escapedArgs = args.map(arg => {
            if (arg.includes(' ') || arg.includes('"') || arg.includes('(') || arg.includes(')')) {
                return `"${arg.replace(/"/g, '\\"')}"`;
            }
            return arg;
        });
        
        const command = `"${this.binaryPath}" ${escapedArgs.join(' ')}`;
        
        try {
            const { stdout, stderr } = await execAsync(command, {
                maxBuffer: 10 * 1024 * 1024, // 10MB buffer
                timeout: 30000 // 30s timeout
            });

            if (stderr && stderr.trim()) {
                console.warn('SQLite Engine Warning:', stderr);
            }

            const result = JSON.parse(stdout.trim());
            return result;
        } catch (error: any) {
            if (error.stdout) {
                try {
                    const result = JSON.parse(error.stdout.trim());
                    return result;
                } catch (parseError) {
                    // Fall through to generic error
                }
            }
            
            throw new Error(`SQLite execution failed: ${error.message}`);
        }
    }

    async connect(): Promise<boolean> {
        try {
            const result = await this.executeBinary([
                '--action', 'connect',
                '--database', this.dbPath
            ]);
            return result.status === 'success';
        } catch (error) {
            return false;
        }
    }

    async exists(): Promise<boolean> {
        try {
            const result = await this.executeBinary([
                '--action', 'exists',
                '--database', this.dbPath
            ]);
            return result.status === 'success' && result.data === true;
        } catch (error) {
            return false;
        }
    }

    async query(sql: string, params?: any[]): Promise<SqliteResult> {
        const args = [
            '--action', 'query',
            '--database', this.dbPath,
            '--query', sql
        ];

        if (params && params.length > 0) {
            args.push('--params', JSON.stringify(params));
        }

        return this.executeBinary(args);
    }

    // Método para múltiples queries (como lo hace better-sqlite3)
    async queryMultiple(sql: string): Promise<SqliteResult> {
        return this.query(sql);
    }

    // Método prepare que simula prepared statements
    prepare(sql: string) {
        return {
            all: async (...params: any[]) => {
                const result = await this.query(sql, params);
                if (result.status === 'error') {
                    throw new Error(result.message);
                }
                return Array.isArray(result.data) ? result.data : [];
            },
            run: async (...params: any[]) => {
                const result = await this.query(sql, params);
                if (result.status === 'error') {
                    throw new Error(result.message);
                }
                
                // Si es un RunResult, devolverlo tal como está
                if (typeof result.data === 'object' && 
                    result.data.hasOwnProperty('changes') && 
                    result.data.hasOwnProperty('lastID')) {
                    return result.data as RunResult;
                }
                
                return { changes: 0, lastID: 0 };
            }
        };
    }

    // Para compatibilidad con better-sqlite3 API sincrona usando deasync si es necesario
    prepareSync(sql: string) {
        // Use __filename for CJS, process.cwd() for ESM
        const requireUrl = typeof __filename !== 'undefined' ? __filename : process.cwd();
        const require = createRequire(requireUrl);
        const deasync = require('deasync');
        
        return {
            all: (...params: any[]) => {
                let result: any;
                let done = false;
                let error: any;

                this.query(sql, params).then(res => {
                    if (res.status === 'error') {
                        error = new Error(res.message);
                    } else {
                        result = Array.isArray(res.data) ? res.data : [];
                    }
                    done = true;
                }).catch(err => {
                    error = err;
                    done = true;
                });

                deasync.loopWhile(() => !done);
                if (error) throw error;
                return result;
            },
            run: (...params: any[]) => {
                let result: any;
                let done = false;
                let error: any;

                this.query(sql, params).then(res => {
                    if (res.status === 'error') {
                        error = new Error(res.message);
                    } else if (typeof res.data === 'object' && 
                               res.data.hasOwnProperty('changes') && 
                               res.data.hasOwnProperty('lastID')) {
                        result = res.data;
                    } else {
                        result = { changes: 0, lastID: 0 };
                    }
                    done = true;
                }).catch(err => {
                    error = err;
                    done = true;
                });

                deasync.loopWhile(() => !done);
                if (error) throw error;
                return result;
            }
        };
    }
}